import React from 'react';
import CreatableSelect from 'react-select/creatable';
import { TagSelectContainer } from './style';

// Catalog tags come from the API as { id, name }. react-select works in { label, value }.
// These helpers keep the conversion in one place so every picker behaves the same.
export const tagsToOptions = (list = []) =>
  (list || [])
    .filter(Boolean)
    .map(t =>
      t.label !== undefined && t.value !== undefined
        ? t
        : { label: t.name, value: t.id },
    );

// react-select value -> the { value, label, __isNew__ } array the backend save expects.
export const optionsToPayload = (options = []) => options || [];

/**
 * Reusable multi-select tag picker (type-to-create). Emits the raw react-select
 * value array (each entry { value, label, __isNew__? }) so callers can send it
 * straight to the backend tag-save endpoints.
 */
const TagSelect = ({
  options = [],
  value = [],
  onChange = () => {},
  placeholder = 'Add Tags',
  label = null,
  isDisabled = false,
  className = '',
  excludeValues = [],
}) => {
  // Tags selected in the counterpart list (e.g. Excluded when this is Required).
  // They must not appear in this dropdown, nor be addable — by id or by name.
  const excludedOpts = tagsToOptions(excludeValues);
  const excludedIds = new Set(excludedOpts.map(o => String(o.value)));
  const excludedLabels = new Set(excludedOpts.map(o => String(o.label).toLowerCase()));
  const isExcluded = o =>
    excludedIds.has(String(o.value)) || excludedLabels.has(String(o.label).toLowerCase());

  const availableOptions = tagsToOptions(options).filter(o => !isExcluded(o));

  const handleChange = newValue => {
    // Drop anything that collides with the counterpart list (blocks forced adds).
    onChange((newValue || []).filter(o => !isExcluded(o)));
  };

  return (
    <TagSelectContainer className={className}>
      {label ? <span className="tag-select-label">{label}</span> : null}
      <CreatableSelect
        className="tag-select-container"
        classNamePrefix="tag-select"
        isMulti
        isDisabled={isDisabled}
        name={label || 'Tags'}
        options={availableOptions}
        value={tagsToOptions(value)}
        placeholder={placeholder}
        onChange={handleChange}
        // Render the menu in a body-level portal so it floats above the modal
        // instead of expanding (and being clipped by) the modal's scroll box.
        menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
        menuPosition="fixed"
        styles={{
          menuPortal: base => ({ ...base, zIndex: 99999 }),
          option: base => ({ ...base, fontSize: 14 }),
        }}
      />
    </TagSelectContainer>
  );
};

export default TagSelect;
